import { useCallback, useMemo, type ChangeEvent } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Divider,
  Stack,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { TwoColumnGrid } from '@ui2/structure'
import { SwitchLabeled, CheckboxLabeled } from '@ui/actions'
import { SwitchLabeledProps } from '@ui/actions/SwitchLabeled'
import {
  useControlStore,
  useModelStore,
  useResultsStore,
  useStructuralPlanningStore,
} from '@editorStores'
import { elementTypeToLabelSingular } from 'src/domain/constants/labels'
import useVisibilityFilterScope from './useVisibilityFilterScope'

// styled switch labeled
const StyledSwitchLabeled = ({ ...props }: SwitchLabeledProps) => {
  return <SwitchLabeled {...props} sx={{ p: '4px' }} />
}

const LABEL_VISIBILITY_ELEMENT_TYPES: ElementTypes[] = [
  'outer_walls',
  'inner_walls',
  'vertical_slabs',
  'vertical_roof_slabs',
  'beams',
  'columns',
  'purlins',
  'rips',
  'lintels',
]

const labelVisibilityOptions = LABEL_VISIBILITY_ELEMENT_TYPES.map(value => ({
  value,
  label: elementTypeToLabelSingular[value] || value,
}))

const VisibilitySwitches = () => {
  // Results-specific visibility controls
  const resultsVisibility = useResultsStore(state => state.resultElementVisibility)
  const changeResultElementVisibility = useResultsStore(
    state => state.changeResultElementVisibility,
  )

  // Label visibility controls
  const labelVisibilityByType = useModelStore(state => state.labelVisibilityModel.visibilityByType)
  const showLabels = useControlStore(state => state.showLabels)
  const setShowLabels = useControlStore(state => state.setShowLabels)
  const showLabelCrossSections = useControlStore(state => state.showLabelCrossSections)
  const setShowLabelCrossSections = useControlStore(state => state.setShowLabelCrossSections)
  const setLabelVisibilityForTypes = useModelStore(state => state.setLabelVisibilityForTypes)

  const selectedLabelTypes = useMemo(() => {
    return LABEL_VISIBILITY_ELEMENT_TYPES.filter(type => labelVisibilityByType[type])
  }, [labelVisibilityByType])
  const handleLabelCheckboxChange = useCallback(
    (type: ElementTypes) => (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target
      if (checked) {
        setLabelVisibilityForTypes([...new Set([...selectedLabelTypes, type])])
      } else {
        setLabelVisibilityForTypes(selectedLabelTypes.filter(selectedType => selectedType !== type))
      }
    },
    [selectedLabelTypes, setLabelVisibilityForTypes],
  )

  const location = useLocation()
  const isResultsView = location.pathname.includes('/results')
  const { showWindLoads, showSupports } = useVisibilityFilterScope()

  const visibility = useControlStore(state => state.structuralPlanningElementVisibility)
  const changeVisibility = useControlStore(state => state.changeStructuralPlanningElementVisibility)
  const windLoadDirection = useStructuralPlanningStore(state => state.windLoadDirection)
  const setWindLoadDirection = useStructuralPlanningStore(state => state.setWindLoadDirection)

  return (
    <>
      {/* Wind-load overlays: hidden on the IFC import page. */}
      {showWindLoads && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
            <StyledSwitchLabeled
              checked={visibility.windLoadZones}
              onChange={() => changeVisibility('windLoadZones', !visibility.windLoadZones)}
              label="Windlasten - CPE-Bereiche"
              data-cy="wind-load-zones-visibility-switch"
            />
            {visibility.windLoadZones && (
              <FormControl component="fieldset">
                <RadioGroup
                  value={windLoadDirection}
                  onChange={e => setWindLoadDirection(e.target.value as WindDirection)}
                  row
                  data-cy="wind-load-direction-radio-group"
                >
                  <FormControlLabel
                    value="Direction0"
                    control={<Radio size="small" />}
                    label="0°"
                    data-cy="wind-load-direction-0"
                  />
                  <FormControlLabel
                    value="Direction90"
                    control={<Radio size="small" />}
                    label="90°"
                    data-cy="wind-load-direction-90"
                  />
                  <FormControlLabel
                    value="Direction180"
                    control={<Radio size="small" />}
                    label="180°"
                    data-cy="wind-load-direction-180"
                  />
                  <FormControlLabel
                    value="Direction270"
                    control={<Radio size="small" />}
                    label="270°"
                    data-cy="wind-load-direction-270"
                  />
                </RadioGroup>
              </FormControl>
            )}
          </Stack>
          <StyledSwitchLabeled
            checked={visibility.horizontalWindLoads}
            onChange={() =>
              changeVisibility('horizontalWindLoads', !visibility.horizontalWindLoads)
            }
            label="Windlasten - Horizontale Lasten"
            data-cy="horizontal-wind-loads-visibility-switch"
          />
        </>
      )}

      {/* Support overlays: hidden on the IFC import and global-loads pages. */}
      {showSupports && (
        <>
          <Divider>Auflager (Vertikal und Zug)</Divider>
          <StyledSwitchLabeled
            checked={visibility.verticalTransmitters}
            onChange={() =>
              changeVisibility('verticalTransmitters', !visibility.verticalTransmitters)
            }
            label="Vertikale Auflager"
          />
          <StyledSwitchLabeled
            checked={visibility.connectors}
            onChange={() => changeVisibility('connectors', !visibility.connectors)}
            label="Zugauflager"
          />
        </>
      )}

      {/* Results-specific visibility controls */}
      {isResultsView && (
        <>
          <Divider>Spezielle Filter für Ergebnisansicht</Divider>
          <StyledSwitchLabeled
            checked={resultsVisibility.anchorIndicators}
            onChange={() =>
              changeResultElementVisibility('anchorIndicators', !resultsVisibility.anchorIndicators)
            }
            label="Überlastete Zuganker"
          />
        </>
      )}

      <Divider>Sonstiges</Divider>
      <Box display="flex" alignItems="center">
        <StyledSwitchLabeled
          checked={showLabels}
          onChange={event => setShowLabels(event.target.checked)}
          label="Bauteilname"
          data-cy="element-labels-visibility-switch"
        />
        {showLabels && (
          <StyledSwitchLabeled
            checked={showLabelCrossSections}
            onChange={event => setShowLabelCrossSections(event.target.checked)}
            label="inkl. Querschnitte"
            data-cy="element-label-cross-sections-switch"
          />
        )}
      </Box>
      {showLabels && (
        <TwoColumnGrid>
          {labelVisibilityOptions.map(({ value, label }) => (
            <CheckboxLabeled
              key={value}
              checked={selectedLabelTypes.includes(value)}
              onChange={handleLabelCheckboxChange(value)}
              label={label}
            />
          ))}
        </TwoColumnGrid>
      )}
    </>
  )
}

export default VisibilitySwitches
